import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mockProducts } from '@/utils/mockData';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { createServerSupabaseClient, getSessionFromSSR, getCurrentUserFromSSR, hasBusinessOrAdminPermission } from '@/services/supabase';

// Make our interface match the structure of mockProducts
interface Product {
  id: string;
  business_id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  stock_quantity: number; // this is called stock_quantity in mockProducts
  unit: string;
  images: string[];
  created_at: string;
  updated_at: string;
  status?: string; // Make this optional since it might not be in all products
}

// Server-side function that can be used with Next.js getServerSideProps
export async function getProductsDataSSR(context: any) {
  try {
    // Create a server-side Supabase client
    const supabaseClient = createServerSupabaseClient(context);
    
    // Get the session using the SSR method
    const session = await getSessionFromSSR(supabaseClient);
    
    // If no session, return null
    if (!session) {
      return { user: null, data: null };
    }
    
    // Get the user using the SSR method
    const user = await getCurrentUserFromSSR(supabaseClient);
    
    // Check if user has proper permissions using the centralized function
    if (!hasBusinessOrAdminPermission(user)) {
      return { user: null, data: null };
    }
    
    // In a real implementation, fetch products data from API
    // For now, return mock data
    const productsData = mockProducts;
    
    return {
      user,
      data: productsData
    };
  } catch (error) {
    console.error('Error in getProductsDataSSR:', error);
    return { user: null, data: null };
  }
}

// Props interface for ProductsList component to accept SSR data
interface ProductsListProps {
  initialUser?: any;
  initialData?: any;
}

const ProductsList: React.FC<ProductsListProps> = ({ initialUser, initialData }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // If we have initialData from SSR, use it directly
    if (initialData && initialUser) {
      setProducts(initialData);
    } else {
      // Otherwise use the existing client-side method
      // Load mock products from the mockData utility
      setProducts(mockProducts);
    }
  }, [initialData, initialUser]);

  // Filter products based on search term
  const filteredProducts = products.filter(product => {
    if (!searchTerm) return true;
    
    return (
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold"></h1>

        <div className="flex flex-col sm:flex-row w-full sm:w-auto space-y-2 sm:space-y-0 sm:space-x-4 items-stretch sm:items-center">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search products..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-sky-blue focus:border-sky-blue sm:text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex space-x-4">
            <Link 
              to="/products/add" 
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-center"
            >
              Add Product
            </Link>
            <Link 
              to="/products/bulk-upload" 
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-center"
            >
              Bulk Upload
            </Link>
          </div>
        </div>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No products found. Add some products to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map(product => (
            <div key={product.id} className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="h-48 bg-gray-200 flex items-center justify-center">
                {product.images && product.images.length > 0 ? (
                  <img src={product.images[0]} alt={product.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="text-gray-400">{product.name.charAt(0)}</div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-bold text-lg mb-1">{product.name}</h3>
                <p className="text-gray-600 text-sm mb-2">{product.description}</p>
                <div className="flex justify-between items-center">
                  <span className="font-bold text-green-600">${product.price.toFixed(2)}</span>
                  <span className="text-sm text-gray-500">Qty: {product.stock_quantity} {product.unit}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductsList;
